<script>
  import { tick } from 'svelte';
  import { auth, login, signup, clearError, setAuthMode } from '../state/auth.svelte.js';
  import { buildSogniSignupUrl, hasTurnstileSiteKey } from '../utils/auth-ui.js';
  import { focusDialog } from '../utils/dialog-focus.js';
  import Turnstile from './Turnstile.svelte';

  let { onClose = () => {} } = $props();

  let dialog = $state(null);

  function focusLoginDialog(node, options) {
    const action = focusDialog(node, options);
    return {
      update: action.update,
      // On successful authentication, the opening control becomes enabled in
      // this same update. Return focus after its DOM attribute has changed.
      destroy() { tick().then(action.destroy); }
    };
  }

  const TURNSTILE_KEY = (import.meta.env.VITE_TURNSTILE_KEY || '').trim();

  let mode = $state(auth.authMode === 'signup' ? 'signup' : 'login');

  // Shared
  let username = $state('');
  let password = $state('');
  // Signup-only
  let email = $state('');
  let subscribe = $state(true);
  let referralCode = $state('');
  let turnstileToken = $state('');
  let rememberMe = $state(true);
  let turnstileRef = $state(null);

  // Turnstile tokens are single-use; after a failed signup the widget must be
  // reset or the next attempt resubmits a spent token and keeps failing.
  function resetTurnstile() {
    turnstileToken = '';
    turnstileRef?.reset();
  }

  const isSignup = $derived(mode === 'signup');
  const canRunInAppSignup = $derived(hasTurnstileSiteKey(TURNSTILE_KEY));

  const loginValid = $derived(username.trim().length > 0 && password.length > 0);
  const signupValid = $derived(
    canRunInAppSignup &&
    username.trim().length > 0 &&
    email.trim().length > 0 &&
    password.length >= 8 &&
    turnstileToken.length > 0
  );

  function switchMode(next) {
    mode = next;
    setAuthMode(next);
    clearError();
    turnstileToken = '';
    if (next === 'signup' && !canRunInAppSignup) {
      // Redirect-only signup must never retain credentials entered in the
      // login view or present them as signup inputs.
      username = '';
      password = '';
      email = '';
      referralCode = '';
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (isSignup) {
      if (!canRunInAppSignup) {
        redirectSignup();
        return;
      }
      if (!signupValid) return;
      // Loading disables the focused form control. Keep focus on the dialog
      // before that transition so Tab cannot reach the page behind it.
      dialog.focus({ preventScroll: true });
      const ok = await signup({
        username: username.trim(),
        email: email.trim(),
        password,
        subscribe,
        turnstileToken,
        referralCode: referralCode.trim() || undefined
      });
      if (ok) {
        onClose();
      } else {
        // Signup failed — the token is now spent; mint a fresh challenge.
        resetTurnstile();
      }
    } else {
      if (!loginValid) return;
      dialog.focus({ preventScroll: true });
      const ok = await login(username.trim(), password, rememberMe);
      if (ok) onClose();
    }
  }

  function handleBackdropClick() {
    if (!auth.isLoading) onClose();
  }

  // Fallback: redirect signup to app.sogni.ai when no Turnstile sitekey is set.
  function redirectSignup() {
    window.location.href = buildSogniSignupUrl(window.location.href);
  }
</script>

<div
  class="login-overlay"
  role="presentation"
  onclick={(event) => { if (event.target === event.currentTarget) handleBackdropClick(); }}
>
  <div
    class="login-dialog"
    bind:this={dialog}
    role="dialog"
    aria-modal="true"
    aria-labelledby="login-title"
    tabindex="-1"
    onkeydown={(event) => event.stopPropagation()}
    use:focusLoginDialog={{ onClose: handleBackdropClick }}
  >
    <div class="dialog-header">
      <h3 id="login-title">{isSignup ? 'Create your Sogni account' : 'Sign in to Sogni'}</h3>
      <button class="close-btn" onclick={onClose} disabled={auth.isLoading} aria-label="Close">×</button>
    </div>

    <form class="dialog-content" onsubmit={handleSubmit}>
      <p class="login-info">
        {#if isSignup}
          One Sogni account works across every Sogni app — including vibe.
        {:else}
          Signing in on any Sogni app signs you in here too.
        {/if}
      </p>

      {#if auth.error}
        <div class="error-message">
          {auth.error}
          <button type="button" class="dismiss-error" onclick={clearError} aria-label="Dismiss error">×</button>
        </div>
      {/if}

      {#if isSignup && !canRunInAppSignup}
        <div class="redirect-signup">
          <strong>Account creation continues on app.sogni.ai.</strong>
          <span>You'll return to Vibe with the same secure Sogni session after signing up.</span>
        </div>
      {:else}
        <div class="form-field">
          <label for="username">Username</label>
          <input
            id="username"
            type="text"
            bind:value={username}
            placeholder="Your Sogni username"
            disabled={auth.isLoading}
            autocomplete="username"
          />
        </div>

        {#if isSignup}
          <div class="form-field">
            <label for="email">Email</label>
            <input
              id="email"
              type="email"
              bind:value={email}
              placeholder="you@example.com"
              disabled={auth.isLoading}
              autocomplete="email"
            />
          </div>
        {/if}

        <div class="form-field">
          <label for="password">Password</label>
          <input
            id="password"
            type="password"
            bind:value={password}
            placeholder={isSignup ? 'At least 8 characters' : 'Your Sogni password'}
            disabled={auth.isLoading}
            autocomplete={isSignup ? 'new-password' : 'current-password'}
          />
          {#if isSignup}
            <span class="field-hint">Your password derives your Sogni wallet — it can't be recovered, so keep it safe.</span>
          {/if}
        </div>

        {#if isSignup}
          <Turnstile bind:this={turnstileRef} sitekey={TURNSTILE_KEY} onToken={(t) => (turnstileToken = t)} />
          <label class="checkbox-row">
            <input type="checkbox" bind:checked={subscribe} disabled={auth.isLoading} />
            <span>Email me product updates</span>
          </label>
        {:else}
          <label class="checkbox-row">
            <input type="checkbox" bind:checked={rememberMe} disabled={auth.isLoading} />
            <span>Keep me signed in</span>
          </label>
        {/if}
      {/if}

      <div class="dialog-actions">
        <button type="button" class="btn-secondary" onclick={onClose} disabled={auth.isLoading}>
          Cancel
        </button>
        <button
          type="submit"
          class="btn-primary"
          disabled={auth.isLoading || (isSignup ? (canRunInAppSignup ? !signupValid : false) : !loginValid)}
        >
          {#if auth.isLoading}
            <span class="spinner"></span>
            {isSignup ? 'Creating…' : 'Signing in…'}
          {:else}
            {isSignup ? (canRunInAppSignup ? 'Create account' : 'Continue to Sogni') : 'Sign in'}
          {/if}
        </button>
      </div>

      <div class="signup-link">
        {#if isSignup}
          <span>Already have an account?</span>
          <button type="button" class="link-btn" onclick={() => switchMode('login')} disabled={auth.isLoading}>Sign in</button>
        {:else}
          <span>New to Sogni?</span>
          <button type="button" class="link-btn" onclick={() => switchMode('signup')} disabled={auth.isLoading}>Create an account</button>
        {/if}
      </div>
    </form>
  </div>
</div>

<style>
  .login-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
    animation: fadeIn 0.15s ease;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .login-dialog {
    background: var(--color-bg);
    border-radius: 16px;
    width: 100%;
    max-width: 400px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-shadow: var(--shadow-lg);
    border: 2px solid var(--color-primary-border);
    animation: slideIn 0.2s ease;
    max-height: 92vh;
    overflow-y: auto;
  }

  @keyframes slideIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid var(--color-border);
  }

  .dialog-header h3 {
    margin: 0;
    color: var(--color-primary);
    font-size: 1.2rem;
  }

  .close-btn {
    background: none;
    border: none;
    color: var(--color-text-muted);
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    transition: all 0.2s;
  }

  .close-btn:hover:not(:disabled) {
    background: var(--color-surface);
    color: var(--color-text);
  }

  .close-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .dialog-content {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .login-info {
    margin: 0;
    font-size: 0.9rem;
    color: var(--color-text-muted);
    line-height: 1.5;
  }

  .error-message {
    background: var(--color-error);
    color: white;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 0.9rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  .dismiss-error {
    background: transparent;
    border: none;
    color: white;
    font-size: 1.25rem;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    opacity: 0.8;
  }

  .dismiss-error:hover { opacity: 1; }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .form-field label {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--color-text);
  }

  .form-field input[type="text"],
  .form-field input[type="email"],
  .form-field input[type="password"] {
    padding: 12px 14px;
    font-size: 1rem;
    border: 2px solid var(--color-border);
    border-radius: 8px;
    background: var(--color-bg);
    color: var(--color-text);
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .form-field input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px var(--color-primary-light);
  }

  .form-field input:disabled { background: var(--color-surface); opacity: 0.7; }

  .field-hint {
    font-size: 0.75rem;
    color: var(--color-text-muted);
    line-height: 1.4;
  }

  .checkbox-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.875rem;
    color: var(--color-text-muted);
    cursor: pointer;
  }

  .checkbox-row input { width: 16px; height: 16px; accent-color: var(--color-primary); }

  .dialog-actions {
    display: flex;
    gap: 12px;
    margin-top: 4px;
  }

  .dialog-actions button {
    flex: 1;
    padding: 12px 16px;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .btn-secondary {
    background: var(--color-surface);
    color: var(--color-text);
    border: 2px solid var(--color-border);
  }

  .btn-secondary:hover:not(:disabled) { border-color: var(--color-primary); color: var(--color-primary); }
  .btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }

  .btn-primary {
    background: var(--color-primary);
    color: white;
    border: none;
  }

  .btn-primary:hover:not(:disabled) {
    background: var(--color-primary-hover);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 27, 141, 0.3);
  }

  .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

  .spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  .redirect-signup {
    margin: 0;
    font-size: 0.85rem;
    color: var(--color-text-muted);
    line-height: 1.5;
    background: var(--color-surface);
    padding: 14px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .redirect-signup strong { color: var(--color-text); }

  .signup-link {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 4px;
    padding-top: 16px;
    border-top: 1px solid var(--color-border);
    font-size: 0.9rem;
    color: var(--color-text-muted);
  }

  .link-btn {
    background: none;
    border: none;
    color: var(--color-primary);
    font-size: inherit;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .link-btn:hover:not(:disabled) { color: var(--color-primary-hover); }
  .link-btn:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
